import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Tooltip,
  Typography,
  useMediaQuery,
  Box,
  Badge,
  Fab,
  Grid2 as Grid,
  CardActionArea,
} from '@mui/material';
import { FilterAlt } from '@mui/icons-material';
import { collection, query, where, orderBy, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { parseDate, renderFieldValue, renderValueCurrency } from '../../utils/utils';
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  Actions,
  useDispatchProvider,
  useStateFiltersCustomerOrders,
  useStateFormCustomer,
} from '../utils/StateProvider';
import DrawerCustomerOrdersFilters from '../drawers/DrawerCustomerOrdersFilters';
import { useRole } from '../utils/AclProvider';
import ListHeaderLabel from '../utils/ListHeaderLabel';
import SkeletonList from '../utils/SkeletonList';
import ListNoResults from '../utils/ListNoResults';

const ListCustomerOrders = () => {
  const role = useRole();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const stateFormCustomer = useStateFormCustomer();
  const stateFiltersCustomerOrders = useStateFiltersCustomerOrders();
  const dispatch = useDispatchProvider();
  const [stateOrderSums, setStateOrderSums] = useState({ total: 0, downPayment: 0 });

  const handleDrawerOrder = (orderId) => () => {
    dispatch && dispatch({ type: Actions.FormOrder, payload: orderId });
    dispatch && dispatch({ type: Actions.TabsOrder, payload: 'products' });
    dispatch && dispatch({ type: Actions.DrawerOrder, payload: true });
  };

  const toggleDrawerFilters = () => {
    dispatch && dispatch({ type: Actions.DrawerCustomerOrdersFilters, payload: true });
  };

  const columns = [
    { id: 'dateCreated', label: 'Date Created' },
    { id: 'userCreated', label: 'User Created' },
    { id: 'dateUpdated', label: 'Date Updated' },
    { id: 'userUpdated', label: 'User Updated' },
    { id: 'dateCompleted', label: 'Date Completed' },
    { id: 'userCompleted', label: 'User Completed' },
    { id: 'orderTransaction', label: 'Transaction' },
    { id: 'comments', label: 'Comments' },
    { id: 'orderStatus', label: 'Status' },
    { id: 'products', label: 'Products' },
    { id: 'downPayment', label: 'Down Payment', align: 'right' },
    { id: 'total', label: 'Total', align: 'right' },
  ];

  const customerRef = doc(db, 'customers', stateFormCustomer);
  const collectionRef = query(
    collection(db, 'orders'),
    // orderBy('dateCreated'),
    where('customerRef', '==', customerRef),
    ...(stateFiltersCustomerOrders.orderStatus
      ? [where('orderStatus', '==', stateFiltersCustomerOrders.orderStatus)]
      : role !== 'admin'
        ? [where('orderStatus', '!=', 'unpaid')]
        : []),
    ...(stateFiltersCustomerOrders.dateCreated
      ? [where('dateCreated', '==', parseDate(stateFiltersCustomerOrders.dateCreated))]
      : []),
    orderBy(stateFiltersCustomerOrders.sortingField, stateFiltersCustomerOrders.sortingOrder),
    // where('products', '==', '4'),
    // limit(5),
  );
  // const collectionRef = collection(db, 'orders');

  const [customerOrdersSnapshot, loading, error] = useCollection(collectionRef, {
    snapshotListenOptions: { includeMetadataChanges: false },
  });

  const calculateTotal = () => {
    return customerOrdersSnapshot?.docs.reduce((partialSum, order) => {
      const orderData = order.data();
      return partialSum + parseFloat(orderData.total);
    }, 0);
  };

  const calculateDownPayment = () => {
    return customerOrdersSnapshot?.docs.reduce((partialSum, order) => {
      const orderData = order.data();
      return partialSum + parseFloat(orderData.downPayment);
    }, 0);
  };

  const getFiltersCount = () => {
    let filtersCount = 0;
    filtersCount += stateFiltersCustomerOrders.dateCreated ? 1 : 0;
    filtersCount += stateFiltersCustomerOrders.orderStatus ? 1 : 0;
    return filtersCount;
  };

  useEffect(() => {
    if (customerOrdersSnapshot !== undefined) {
      setStateOrderSums({
        total: calculateTotal(),
        downPayment: calculateDownPayment(),
      });
    }
  }, [customerOrdersSnapshot]);

  useEffect(() => {
    if (error) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error fetching orders collection: ${error}`,
          },
        });
    }
  }, [error]);

  const mapColumnGridXs = (column) => {
    switch (column.id) {
      case 'orderStatus':
      case 'dateCreated':
      case 'dateCompleted':
      case 'products':
      case 'total':
      case 'downPayment':
        return 4;
      default:
        return 12;
    }
  };

  return (
    <>
      <Box sx={{ overflowY: 'auto', height: '100%' }}>
        {!isMobile && (
          <Box
            position={'sticky'}
            top={0}
            background={'background.surface'}
            zIndex={1}
          >
            <Card elevation={0}>
              <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Grid
                  container
                  spacing={2}
                >
                  {columns.map((column) => {
                    if (stateFiltersCustomerOrders.columns[column.id]) {
                      return (
                        <Grid
                          size={'grow'}
                          key={column.id}
                        >
                          <ListHeaderLabel column={column} />
                        </Grid>
                      );
                    } else {
                      return null;
                    }
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
        <Grid
          container
          spacing={2}
        >
          {loading && <SkeletonList />}
          {!loading && customerOrdersSnapshot.docs?.length === 0 && <ListNoResults />}
          {!loading &&
            customerOrdersSnapshot?.docs.map((order) => {
              return (
                <Grid
                  size={12}
                  key={order.id}
                >
                  <Card data-id={order.id}>
                    <CardActionArea onClick={handleDrawerOrder(order.id)}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Grid
                          container
                          spacing={2}
                        >
                          {columns.map((column) => {
                            const data = order.data();
                            data.orderTransaction = order.ref;
                            if (stateFiltersCustomerOrders.columns[column.id]) {
                              return (
                                <Grid
                                  size={{ xs: mapColumnGridXs(column), md: 'grow' }}
                                  key={column.id}
                                >
                                  {isMobile && (
                                    <Typography
                                      color={'textSecondary'}
                                      variant={'body2'}
                                      align={isMobile ? 'left' : column.align}
                                    >
                                      {column.label}
                                    </Typography>
                                  )}
                                  {renderFieldValue(column.id, data[column.id], isMobile ? 'left' : column.align)}
                                </Grid>
                              );
                            } else {
                              return null;
                            }
                          })}
                        </Grid>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
        </Grid>

        {!isMobile && (
          <Box
            position={'sticky'}
            bottom={0}
            background={'background.surface'}
            zIndex={1}
          >
            <Card elevation={0}>
              <CardContent sx={{ px: 2, py: 2, '&:last-child': { pb: 2 } }}>
                <Grid
                  container
                  spacing={2}
                >
                  {columns.map((column) => {
                    if (stateFiltersCustomerOrders.columns[column.id]) {
                      return (
                        <Grid
                          size={'grow'}
                          key={column.id}
                        >
                          {column.id === 'downPayment' && (
                            <Typography
                              color={'textSecondary'}
                              variant={'subtitle2'}
                              sx={{ fontWeight: 'bold' }}
                              align={isMobile ? 'left' : column.align}
                            >
                              {`Total: ${renderValueCurrency(stateOrderSums.downPayment)}`}
                            </Typography>
                          )}
                          {column.id === 'total' && (
                            <Typography
                              color={'primary'}
                              variant={'subtitle2'}
                              sx={{ fontWeight: 'bold' }}
                              align={isMobile ? 'left' : column.align}
                            >
                              {`Total: ${renderValueCurrency(stateOrderSums.total)}`}
                            </Typography>
                          )}
                        </Grid>
                      );
                    } else {
                      return null;
                    }
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
        <Box
          position={'fixed'}
          bottom={24}
          right={24}
          zIndex={1}
        >
          <Tooltip title={'Sorting and Filtering'}>
            <Badge
              badgeContent={getFiltersCount()}
              color="primary"
            >
              <Fab
                color={'primary'}
                onClick={toggleDrawerFilters}
                size={'small'}
              >
                <FilterAlt />
              </Fab>
            </Badge>
          </Tooltip>
        </Box>
      </Box>
      <DrawerCustomerOrdersFilters />
    </>
  );
};

export default ListCustomerOrders;
